// featureStates.js — the four-state feature filter (owner ruling, 2026-08-27).
//
//   "what if first click selects what has that feature, second one selects what has not,
//    third what has not declared, forth deselect"
//
// So one control cycles  unset → yes → no → unknown → unset,  and — the owner's amendment the
// same day — CLICKING THE LABEL clears straight back to unset, because a four-state cycle makes
// undoing a mis-click cost three more clicks and the label is the obvious escape hatch.
//
// WHY THIS IS A MODULE AND NOT AN onChange HANDLER. The cycle is the whole feature; if it is
// wrong the agent silently searches for the opposite of what they meant. It is pure, so it is
// tested (featureStates.test.mjs), and every surface — panel, chips, URL restore, saved
// searches — asks THIS, never re-derives the order.
//
// 🔑 THE THREE STATES ARE THE THREE ANSWERS THE DATA ALREADY HELD, not an invention:
//   yes      property_feature.present = true    "recorded as having it"
//   no       property_feature.present = false   351,161 rows / 25,035 properties carry this
//   unknown  no row at all                      "nobody has said"
// The search computed all three per code since long before it could be asked for any but the
// first (0756 / v59 adds p_feature_states). ⚠️ `unknown` is a DEFINITE question with a definite
// answer — it must never route its own matches to the possible-matches shelf, which is why the
// SQL treats it differently from the other two. See the migration header.
//
// ⚠️ MIGRATION, one-way and deliberate: a code selected before this shipped lives in
// `featureCodes` (a bare list meaning "must be true"). `stateOf` reads that as 'yes' so old
// URLs, saved searches and restored cards keep working; `cycle` moves the code OUT of
// featureCodes and into featureStates on the first touch. The two params still compose
// server-side (the RPC ANDs them), so a half-migrated filter set is correct, never contradictory.
// featureCodes is NOT retired: the apartment-status axis rides it (IntakeTab ~3612) and means
// something different.

export const FEATURE_STATE_ORDER = ['yes', 'no', 'unknown']

// 🔴 A STATE THE CORPUS CANNOT ANSWER MUST NOT BE OFFERED (0758).
// The cycle above assumes all three states are askable of every code. Measured 2026-08-28, that
// is false for most of them: of 91 codes the panel offers, only 32 have a single recorded
// ABSENCE. For the other 59 the ✕ control returns zero and is indistinguishable from "no such
// flats exist" — the same disease as a lock naming an action the viewer does not have, and the
// same shape as every zero-that-looks-like-an-answer this codebase keeps paying for.
//   pool            167 present / 23,752 absent   ← absence is the MORE useful state
//   semi_basement   139 present /      0 absent   ← the ✕ control is dead
// 'yes' and 'unknown' are always answerable: a code in the panel has present rows by definition
// (it cleared the usage bar), and "nobody said" is answerable for every code by construction.
// So only 'no' is ever withheld, and it is withheld from the CYCLE, not merely hidden — a state
// you cannot see must also be one you cannot land on by clicking past it.
export function statesFor(feature) {
  const absent = Number(feature?.usage_absent) || 0
  return absent > 0 ? FEATURE_STATE_ORDER : ['yes', 'unknown']
}

const entryFor = (states, code) => (states || []).find(e => e.split(':')[0] === code)

/** 'yes' | 'no' | 'unknown' | null — null means the code is not filtered on at all. */
export function stateOf(filters, code) {
  const e = entryFor(filters?.featureStates, code)
  if (e) {
    const w = e.split(':')[1]
    return FEATURE_STATE_ORDER.includes(w) ? w : null
  }
  // legacy: a bare featureCodes membership is exactly "has it"
  return (filters?.featureCodes || []).includes(code) ? 'yes' : null
}

/** The next state in the cycle. Exported so a tooltip can name what the NEXT click does. */
export function nextState(cur, allowed = FEATURE_STATE_ORDER) {
  if (cur == null) return allowed[0] ?? null
  const i = allowed.indexOf(cur)
  // A state that is no longer allowed (a stale URL, or coverage that has since gone to zero)
  // exits the cycle rather than trapping it: the next click clears.
  if (i < 0) return null
  return i === allowed.length - 1 ? null : allowed[i + 1]
}

function write(filters, code, want) {
  const states = (filters?.featureStates || []).filter(e => e.split(':')[0] !== code)
  // the code leaves featureCodes whichever way it goes — one code must never be asserted twice
  // in two different vocabularies, which would AND "has it" with "does not have it" and return
  // nothing at all, looking exactly like an empty market.
  const codes = (filters?.featureCodes || []).filter(c => c !== code)
  return {
    featureStates: want ? [...states, `${code}:${want}`] : states,
    featureCodes: codes,
  }
}

/** Advance one step: unset → yes → no → unknown → unset. Returns a filters PATCH. */
export function cycle(filters, code, allowed = FEATURE_STATE_ORDER) {
  return write(filters, code, nextState(stateOf(filters, code), allowed))
}

/** Straight back to unset — the label click. Returns a filters PATCH. */
export function clear(filters, code) {
  return write(filters, code, null)
}

/** How many feature questions are being asked, for the facet badge. Counts both vocabularies. */
export function activeCount(filters) {
  const stated = new Set((filters?.featureStates || []).map(e => e.split(':')[0]))
  for (const c of (filters?.featureCodes || [])) stated.add(c)
  return stated.size
}
